import React from 'react'
import { Alert, Box, Stack, Typography } from '@mui/material'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import InventoryIcon from '@mui/icons-material/Inventory2Outlined'
import { StockSearchContext } from './types/types'
import { Stock } from '../../../interfaces'
import { ApiResponse } from '../../../typings/ApiResponse'
import { SkuCard } from './Components/SkuCard'
import { positionResultsStyles as s } from './Styles/StockByPositionResults.styles'
import { InfoChip } from '../../../components/_WMS/InfoChip/InfoChip'
import { eTypePosition } from '../../../enums/eTypePosition'
import { BackButtonBase } from '../../../components/_WMS/BackButton/BackButtonBase'

interface StockByPositionResultsProps {
  data: ApiResponse<Stock>
  context: StockSearchContext
  onBack: () => void
}

// ── Main component ────────────────────────────────────────────────────────────

const StockByPositionResults: React.FC<StockByPositionResultsProps> = ({
  data,
  context,
  onBack
}) => {
  const { items, totalItems } = data
  const totalQuantityDisplay = items.find(
    (stock) => stock.totalQuantity !== '00' && stock.totalQuantity !== undefined
  )?.totalQuantity
  const isEmpty = items.length === 0

  return (
    <Stack spacing={0.5}>
      {/* Section label */}
      <Typography variant='h6' component='h1' sx={s.sectionLabel}>
        Ubicación consultada
      </Typography>

      {/* Context card */}
      <Box sx={s.contextCard}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          mb={1.5}
        >
          <BackButtonBase onBack={onBack} />
          <InfoChip
            label={`Bodega: ${context.selectedLabel}`}
            sx={s.infoChip}
          />
        </Stack>

        <Stack alignItems='center' spacing={0.75} justifyContent='center'>
          <InfoChip
            label={`Ubicación: ${context.searchValue}`}
            sx={s.infoChip}
          />
          {data?.items[0]?.typePositionId === eTypePosition.Venta && (
            <InfoChip label='Ubicación de Venta' sx={s.infoChip} />
          )}
        </Stack>

        {context.locationActive === false && (
          <Alert
            severity='warning'
            icon={<WarningAmberRoundedIcon fontSize='small' />}
            sx={s.warningAlert}
          >
            Esta ubicación se encuentra desactivada.
          </Alert>
        )}
      </Box>

      {/* Empty state */}
      {isEmpty ? (
        <Box sx={s.emptyState}>
          <InventoryIcon sx={s.emptyIcon} />
          <Typography fontSize={13} color='text.secondary'>
            No se encontraron productos en esta ubicación.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Metrics */}
          <Stack direction='row' spacing={1.5}>
            {[
              { label: 'SKUs', value: totalItems },
              { label: 'Uds. totales', value: totalQuantityDisplay }
            ].map(({ label, value }) => (
              <Box key={label} sx={s.metricCard}>
                <Typography sx={s.metricLabel}>{label}</Typography>
                <Typography sx={s.metricValue}>{value}</Typography>
              </Box>
            ))}
          </Stack>

          {/* SKU cards */}
          <Stack spacing={0.5}>
            {items.map((item) => (
              <SkuCard
                key={`${item.stockKeepingUnitId}-${item.positionId}`}
                item={item}
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  )
}

export default StockByPositionResults
